import React, { FC, useEffect, useState } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import { ReactComponent as UnitedKingdomFlagIcon } from '../../../../../assets/united-kingdom-flag.svg';
import GenericTextField from '../../../../../components/GenericTextField/GenericTextField';
import GenericButton, { GenericButtonTypeEnum } from '../../../../../components/GenericButton';
import { createNotification } from '../../../../../utilities/helper/notification.helper';
import { MESSAGE_TYPE } from '../../../../../common/constants/common.constants';
import { EditTitleBox } from './EditTitleModal.styles';
import { LanguageCodeEnum } from '../../../../../common/enums/common.enum';
import { languageMeta } from '../../../../../common/constants/language.constants';
import { UpdatedTitle } from '../EventDetails/EventDetails';
import { LocalizedTitle } from '../../../../../utilities/helper/language.helper';
import { useTranslation } from 'react-i18next';

interface EditTitleModalProps {
  open: boolean;
  type: 'option' | 'childEvent';
  onClose: () => void;
  option?: LocalizedTitle;
  secondaryLang: LanguageCodeEnum
  onSave: (updated: UpdatedTitle) => Promise<void> | void;
}

const EditTitleModal: FC<EditTitleModalProps> = ({ open, type, onClose, option, onSave, secondaryLang}) => {
  const { t } = useTranslation();
  const [engTitle, setEngTitle] = useState('');
  const [secondaryTitle, setSecondaryTitle] = useState('');

  useEffect(() => {
    if(open) {
    if (option) {
      setEngTitle(option.eng || '');
      setSecondaryTitle(option[secondaryLang] || '')
    } else {
      setEngTitle('');
      setSecondaryTitle('')
      }
    }
  }, [open, option, secondaryLang]);

  const handleSave = async () => {
    try {
      if (!engTitle.trim()) {
        createNotification(MESSAGE_TYPE.ERROR, t('events.validation.englishTitleRequired'));
        return;
      }

      await onSave({
        eng: engTitle.trim(),
        secondary: {
          lang: secondaryLang,
          value: secondaryTitle.trim()
        }
      });
      if (type === 'option') {
        createNotification(MESSAGE_TYPE.SUCCESS, t('events.optionTitlesUpdated'));
      } else {
        createNotification(MESSAGE_TYPE.SUCCESS, t('events.childEventTitlesUpdated'));
      }

      onClose();
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('events.titlesUpdateFailed'));
    }
  };
  const {Flag} = languageMeta[secondaryLang];

  return (
    <Modal open={open} onClose={onClose}>
      <EditTitleBox>
        <Typography variant="h6">{type === 'childEvent' ? t('events.editChildEvent') : t('events.editOption')}</Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            pt: 1.5,
            gap: 1.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <GenericTextField label={t('events.englishTitleRequiredLabel')} value={engTitle} onChange={e => setEngTitle(e.target.value)} fullWidth />
            <UnitedKingdomFlagIcon width={27} height={20} style={{ marginBottom: '10px' }} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <GenericTextField
              label={t('events.titleInLanguage', { language: t(`language.names.${secondaryLang}`) })}
              value={secondaryTitle}
              onChange={e => setSecondaryTitle(e.target.value)}
              fullWidth
            />
            <Flag width={27} height={20} style={{ marginBottom: '10px' }} />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: 2,
            gap: 1.5
          }}
        >
          <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="text" onClick={onClose} label={t('common.cancel')} />
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            isActive={!!engTitle.trim()}
            onClick={handleSave}
            label={t('common.save')}
            disabled={!engTitle.trim()}
          />
        </Box>
      </EditTitleBox>
    </Modal>
  );
};

export default EditTitleModal;
